import { Icon, TextField, Spinner, Checkbox } from '@scale/scaleui';
import { SearchResult } from '@scale/shared/types/chat';
import { AsyncState } from 'react-use-v13/lib/useAsyncFn';

interface SearchResultsSectionProps {
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  readOnly: boolean;
  searchQueryResults: SearchResult[];
  disableFetchSearchResults: boolean;
  searchState: AsyncState<void>;
  fetchSearchResults: (searchTerm?: string, modelId?: string) => Promise<void>;
  onCheckboxChange: (index: number) => void;
  resultIndex: number;
  searchModelId: string;
}

const SearchResultsSection: React.FC<SearchResultsSectionProps> = ({
  searchQuery,
  setSearchQuery,
  readOnly,
  searchQueryResults,
  disableFetchSearchResults,
  searchState,
  fetchSearchResults,
  onCheckboxChange,
  resultIndex,
  searchModelId,
}) => {
  return (
    <>
      <div className="flex flex-row items-center justify-center gap-1 w-full">
        <TextField
          id="text-field"
          className="flex-1"
          disabled={readOnly}
          onChange={e => {
            setSearchQuery(e.target.value);
          }}
          placeholder="Enter a search query"
          type="text"
          value={searchQuery}
        />
        {searchState.loading && (
          <div className="w-10 h-10 flex items-center justify-center">
            <Spinner size="sm" variant="primary" />
          </div>
        )}
        {!searchState.loading && (
          <Icon
            icon={['far', 'search']}
            size="lg"
            variant="neutral"
            disabled={disableFetchSearchResults}
            onClick={async () => {
              await fetchSearchResults(searchQuery, searchModelId);
            }}
          />
        )}
      </div>
      <div className="flex-1">
        {searchState.error && (
          <div className="flex justify-center items-center text-danger-400">
            <Icon variant="danger" size="sm" icon="exclamation-circle" />
            Error fetching search results. Please try again after a few seconds.
          </div>
        )}
        {!searchState.loading && !searchState.error && (
          <div className="flex flex-col gap-4">
            {searchQueryResults.map((result, index) => (
              <div key={index}>
                <div className="flex flex-row items-center gap-2">
                  <Checkbox
                    checked={searchQueryResults[index].isChecked}
                    disabled={readOnly}
                    id={`checkbox-${index}`}
                    onChange={() => {
                      onCheckboxChange(index);
                    }}
                  />
                  <a
                    className="text-info-600 no-underline hover:underline break-all"
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`[${resultIndex}.${index + 1}] ${
                      result.title ?? result.sourceTitle ?? result.link ?? result.url ?? ''
                    }`}
                  </a>
                </div>
                <div className="text-sm text-neutral-700 break-words">{result.snippet}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResultsSection;
